import React, { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';

import { Button, PopoverSheet, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { shoppingAssistantCTAClick } from 'reaxl-analytics-handlers';

import { kbbVRSDataDuck, paymentsDuck } from '@/ducks';

import {
    srpActiveInteractionDuck,
    srpFiltersDuck,
    srpStrongAttributesDuck,
    srpSuggestedFiltersDuck,
} from '@/ducks/srp';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import LazyComponent from '@/components/LazyComponent';

const CompareTrimsLink = dynamic(() => import(
    /* webpackChunkName: "CompareTrimsLink" */
    '@/components/CompareTrimsLink'
));

const CompareTrimsContainer = dynamic(() => import(
    /* webpackChunkName: "CompareTrimsContainer" */
    '@/containers/CompareTrimsContainer'
    , { ssr: false }
));

const SaveSearchContainer = dynamic(() => import(
    /* webpackChunkName: "SaveSearchContainer" */
    '@/containers/srp/SaveSearchContainer'
    , { ssr: false }
));

const KeywordSearchContainer = dynamic(() => import(
    /* webpackChunkName: "KeywordSearchContainer" */
    '@/containers/srp/KeywordSearchContainer'
    , { ssr: false }
));

const getMakeModelLabel = (selectedModel) => {
    if (!selectedModel) {
        return null;
    }

    const makeLabel = selectedModel.label?.split(' ')?.[0];
    const modelLabel = selectedModel.options?.find((option) => option.selected)?.label;

    return !!(makeLabel && modelLabel) && `${makeLabel} ${modelLabel}`;
};

function ShoppingAssistantContainer({
    show = false,
    containerElement = null,
    onHide = () => { },
    ...rest
}) {
    const dispatch = useDispatch();
    const navigateToSrp = useSrpNavigation();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const { isBrand } = useBrand();
    const isKbb = isBrand('kbb');

    const compareTrims = useSelector(kbbVRSDataDuck.selectors.getCompareTrims);

    const showTrimsModal = useSelector(srpActiveInteractionDuck.selectors.getShowTrimsModal);

    const firstSuggestedFilter = useSelector(srpSuggestedFiltersDuck.selectors.getFirstSuggestion);
    const strongAttributeFilter = useSelector(srpStrongAttributesDuck.selectors.getFirstStrongAttributesFilter);
    const suggestedFilter = Object.keys(strongAttributeFilter).length > 0 ? strongAttributeFilter : firstSuggestedFilter;

    const modelFilterOptions = useSelector(srpFiltersDuck.selectors.getModelFilterOptions, _isEqual);
    const selectedModel = Object.values(modelFilterOptions)?.[0];
    const makeModelLabel = useMemo(() => getMakeModelLabel(selectedModel), [selectedModel]);

    const compareTrimTitle = `Compare ${makeModelLabel} Trims`;
    const suggestedFilterTitle = `Search ${suggestedFilter.name || ''} ${makeModelLabel || 'Vehicle'}s`;
    const shopByBudgetTitle = 'Shop by Budget';
    const priceAlertTitle = 'Receive Price Alerts';

    const handleSendClick = useCallback((event, input) => {
        sendClick(shoppingAssistantCTAClick, event, {
            input: input || event.rawText,
            isKbb,
        });
    }, [isKbb]);

    const handleHide = useCallback(() => {
        onHide();
    }, [onHide]);

    const handleCompareTrimsClick = useCallback((event) => {
        // TODO: analytics event

        // Always hide the container first for better user experience
        handleHide();
        handleSendClick(event, compareTrimTitle);

        // Only show the modal if compareTrims data is available
        if (compareTrims && Object.keys(compareTrims).length > 0) {
            // Dispatch after a small delay to ensure container is fully hidden
            setTimeout(() => {
                dispatch(srpActiveInteractionDuck.creators.setKeys({
                    activeModel: selectedModel?.value,
                    showTrimsModal: true,
                }));
            }, 500); // Short delay to improve transition appearance
        }
    }, [dispatch, selectedModel, handleHide, handleSendClick, compareTrims, compareTrimTitle]);

    const handleShopByBudgetClick = useCallback((event) => {
        // TODO: analytics event

        dispatch(paymentsDuck.creators.updateMyWallet({ displayModal: true }));
        handleHide();
        handleSendClick(event, shopByBudgetTitle);
    }, [dispatch, handleHide, handleSendClick]);

    const handleSuggestedFilterClick = useCallback(async (event) => {
        // TODO: analytics event

        handleHide();
        handleSendClick(event, suggestedFilterTitle);

        await navigateToSrp({
            resetPagination: true,
            scroll: !isXs,
            scrollToResultsCount: isXs,
            isNewSearch: true,
            filtersValues: suggestedFilter?.codes,
            action: 'merge',
        });
    }, [handleHide, handleSendClick, suggestedFilterTitle, navigateToSrp, isXs, suggestedFilter]);

    const handlePriceAlertClick = useCallback((event) => {
        handleHide();
        handleSendClick(event, priceAlertTitle);
    }, [handleHide, handleSendClick]);

    const handleKeywordSearch = useCallback((event) => {
        handleHide();
        handleSendClick(event);
    }, [handleHide, handleSendClick]);

    const compareTrimsCta = (
        <LazyComponent>
            <CompareTrimsLink
                trimFilterOptions={selectedModel}
                bsStyle="default"
                bsSize="small"
                className=""
                onClick={handleCompareTrimsClick}
                data-cmp="compare-trims-link"
            >
                {compareTrimTitle}
            </CompareTrimsLink>
        </LazyComponent>
    );

    const suggestedFilterCta = (
        <Button
            bsStyle="default"
            bsSize="small"
            onClick={handleSuggestedFilterClick}
            data-cmp="suggested-filter-button"
        >
            {suggestedFilterTitle}
        </Button>
    );

    const shopByBudgetCta = (
        <Button
            bsStyle="default"
            bsSize="small"
            onClick={handleShopByBudgetClick}
        >
            {shopByBudgetTitle}
        </Button>
    );

    const priceAlertCta = (
        // TODO: verify we can reuse existing analytics event
        <LazyComponent>
            <SaveSearchContainer
                key="saveSearchContainer"
                bsStyle="default"
                bsSize="small"
                isIconCta={false}
                onClickCapture={handlePriceAlertClick}
            >
                {priceAlertTitle}
            </SaveSearchContainer>
        </LazyComponent>
    );

    const keywordSearch = (
        // TODO: verify we can reuse existing analytics events
        <LazyComponent>
            <KeywordSearchContainer
                suppressModal
                suppressClickEvent
                onSearch={handleKeywordSearch}
            />
        </LazyComponent>
    );

    const compareTrimsContainer = (
        <LazyComponent>
            <CompareTrimsContainer modal />
        </LazyComponent>
    );

    const headingText = 'Need help? Try these.';

    return (
        <>
            {showTrimsModal && compareTrimsContainer}
            <PopoverSheet
                role="menu"
                show={show}
                containerElement={containerElement}
                hideCTA
                heading={headingText}
                data-cmp="shopping-assistant-container"
                popoverTriggerId="shopping-assistant-entry"
                id="shopping-assistant-container"
                onHide={handleHide}
                placement="top"
                {...rest}
            >
                <div className="display-flex flex-column flex-grow text-center gap-3">
                    {!isXs && (
                        <Text
                            data-cmp="shopping-assist-dt-heading"
                            color="primary"
                            size={500}
                            weight="ultra-bold"
                        >
                            {headingText}
                        </Text>
                    )}
                    {selectedModel && compareTrimsCta}
                    {suggestedFilter?.codes && suggestedFilterCta}
                    {shopByBudgetCta}
                    {priceAlertCta}
                    {keywordSearch}
                </div>
            </PopoverSheet>
        </>
    );
}

ShoppingAssistantContainer.propTypes = {
    /**
     * if true, the component will be shown
     * @default false
     */
    show: PropTypes.bool,
    /**
     * An html element that is the container of the sheet
     */
    containerElement: PropTypes.element,
    /**
     * The onHide callback will handle functionality associated with the closing of the container
     */
    onHide: PropTypes.func,
};

export default React.memo(ShoppingAssistantContainer, _isEqual);
